import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useToggleSettingUserValidMutation } from '../../store/api/settingsApi'

interface SettingsUserValidDialogProps {
  open: boolean
  email: string
  nextValidText: 'valid' | 'invalid'
  onClose: () => void
  onConfirmed?: () => void
  /** Permission gating (Layer 4) — false → ẩn Confirm + read-only banner. */
  canEdit?: boolean
}

const headerButtonSx = {
  minHeight: 28,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
} as const

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to update user status.'
  if (!('data' in error)) return 'Failed to update user status.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to update user status.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  return 'Failed to update user status.'
}

export default function SettingsUserValidDialog({
  open,
  email,
  nextValidText,
  onClose,
  onConfirmed,
  canEdit = true,
}: SettingsUserValidDialogProps) {
  const [toggleValid, { isLoading }] = useToggleSettingUserValidMutation()
  const [error, setError] = useState('')
  const confirmText = useMemo(
    () => `Do you want to ${nextValidText} Username = '${email}' ?`,
    [nextValidText, email],
  )

  useEffect(() => {
    if (!open) return
    setError('')
  }, [open, email, nextValidText])

  const handleConfirm = async () => {
    if (!canEdit) {
      setError("You don't have permission for this action.")
      return
    }
    setError('')
    try {
      await toggleValid({
        email,
        body: { isActive: nextValidText === 'valid' ? 0 : 1 },
      }).unwrap()
      onConfirmed?.()
      onClose()
    } catch (errorValue) {
      setError(extractApiErrorMessage(errorValue))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography component="span" sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>
          Valid/Invalid Account
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={headerButtonSx}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={!canEdit || isLoading}
            sx={headerButtonSx}
          >
            {isLoading ? <CircularProgress size={14} color="inherit" /> : nextValidText === 'valid' ? 'Mark Valid' : 'Mark Invalid'}
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiTypography-body2': { fontSize: '0.82rem' },
        }}
      >
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {!canEdit ? (
            <Alert severity="info">
              Read-only — you don't have edit permission for this module.
            </Alert>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {confirmText}
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

